/**
 * Staff Console Types (OptiEdge MDM Phase 1)
 *
 * Cross-tenant shapes consumed by `apps/staff/` (admin console at
 * staff.optisigns.com). Server enforces access via `AdminRoleGuard` on every
 * `staff*` resolver. Customer-facing Device type never exposes vpnIp /
 * vpnPublicKey — those are attached at the staff layer via left-join on
 * mdm-devices.
 */

// --- Admin roles ---

export const ADMIN_ROLES = {
  ROOT: 'root',
  ENGINEER: 'engineer',
  SUPPORT: 'support',
  READONLY: 'readonly',
} as const;

export type AdminRole = (typeof ADMIN_ROLES)[keyof typeof ADMIN_ROLES];

export interface AdminUser {
  _id: string;
  email: string;
  role: AdminRole;
  invitedBy: string; // email, or '(seed)' for bootstrapped root users
  invitedAt: string;
  lastLoginAt?: string;
  disabled: boolean;
  createdAt: string;
  updatedAt: string;
}

// Lightweight shape returned by staffMe for sidebar/role gating.
export interface AdminMe {
  email: string;
  role: AdminRole;
  disabled: boolean;
}

// --- Customer search (three independent columns) ---

/** One row per company — deduped by accountId by the backend. */
export interface AccountSearchResult {
  accountId: string;
  companyName?: string;
  companyEmail?: string;
}

/** One row per user — no dedupe. companyName/companyEmail belong to that user's account. */
export interface UserSearchResult {
  userId: string;
  email: string;
  accountId: string;
  companyName?: string;
  companyEmail?: string;
}

/** One row per device — Atlas Search + UUID exact match merged server-side. */
export interface DeviceSearchResult {
  deviceId: string;
  deviceName?: string;
  UUID?: string;
  accountId: string;
  companyName?: string;
}

// Company header + rollups for the customer drill-in page.
export interface StaffCustomer {
  accountId: string;
  companyName: string;
  plan?: string;
  totalDevices: number;
  onlineDevices: number;
  staleDevices: number;
  offlineDevices: number;
  totalFleets: number;
}

// --- Staff device shape (with VPN fields joined from mdm-devices) ---

import type { DeviceClassification } from './device-classification';

export interface StaffDevice {
  _id: string;
  accountId?: string;
  teamId?: string;
  UUID?: string;
  deviceName?: string;
  status?: number;
  os?: string;
  osVersion?: string;
  model?: string;
  platform?: string;
  serialNo?: string;
  lastHeartBeat?: string;
  activationDate?: string;
  manufacturer?: string;
  localAppVersion?: string;
  tags?: string[];
  tz?: string;
  usedStorage?: number;
  totalStorage?: number;
  vpnIp?: string;
  vpnRegion?: string;
  vpnPublicKey?: string;
  vpnCreatedAt?: string;
  fleetId?: string | null;
  // OptiEdge MDM Phase A (spec 04 §3, §4.3) — resolved by the backend
  // DeviceClassificationResolver + StaffDeviceCapabilityResolver, selected
  // by STAFF_CUSTOMER_DEVICES. All four are optional so Phase 1-only callers
  // continue to compile.
  classification?: DeviceClassification | null;
  availableCommands?: string[];
  reportedCapabilities?: string[] | null;
  /**
   * Staff-only: matrix-allowed commands (plan ∩ classification ∩ deviceStateGate),
   * excluding reported-narrowing and fleet deny-list. Used by the 3-column
   * capability diagnostic panel on the staff device detail page. The backend
   * only populates it on the staff `@ResolveField` (see
   * `staff-device-capability.resolver.ts`).
   */
  matrixCapabilities?: string[];
}

// --- WireGuard / Network ---

// Employee peer row (mdm-devices row with kind='employee') — also used for
// device peers (kind='device'); shape is identical and ownerEmail is simply
// null for devices. deviceName/accountId are populated only by
// staffDevicePeers via a left-join to the `devices` collection.
//
// publicKey is nullable as defense-in-depth: the kind filter (added 2026-04-30)
// prevents cloud-room rows (which have no publicKey) from leaking into these
// queries, but a single mis-tagged row should never wipe an entire response.
export interface EmployeePeer {
  _id: string;
  publicKey?: string;
  name: string;
  ownerEmail?: string;
  vpnIp: string;
  subnet: string;
  region: string;
  createdByRole: string;
  createdAt: string;
  lastHandshakeAt?: string;
  deviceName?: string;
  accountId?: string;
  // 'employee' | 'device' | 'room' — explicit row-type discriminator. Kept
  // wide so adding new kinds doesn't ripple through every consumer.
  kind?: string;
}

// Paginated device peers (mdm-devices row where subnet = 'devices').
export interface DevicePeerConnection {
  items: EmployeePeer[];
  total: number;
}

// Subnet utilization for /staff/network/subnets. `total` is null when the
// subnet is infinite (e.g. /10); UI renders as "N / ∞".
export interface Subnet {
  name: string;
  cidr: string;
  type: 'person' | 'device';
  allowProvisioner: boolean;
  used: number;
  total?: number;
}
